import {USER_KEYS} from "@/api/userApi";
import Button from "@/components/Common/Button";
import TextButton from "@/components/Common/TextButton";
import Loader from "@/components/Common/kpi/Loader";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useLogin} from "@/hooks/useLogin";
import {useIsLoggedIn} from "@/hooks/useUserHooks";
import {isFinishRegistrationModalVisibleAtom} from "@/store/jotaiAtoms";
import useRegistrationStore from "@/store/registration";
import type {TUserState} from "@/store/types";
import dispatchGoogleAnalyticsEvent from "@/utils/dispatchGoogleAnalyticsEvent";
import {useMutation} from "@tanstack/react-query";
import type {AxiosError} from "axios";
import {useSetAtom} from "jotai";
import {useCallback, useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router";
import {useShallow} from "zustand/shallow";

const RegistrationBottomView: FC<{isInsideModal?: boolean}> = ({isInsideModal = false}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {login} = useLogin();
  const {refetch: refetchUser} = useFetchUser();
  const setIsFinishRegistrationModalVisible = useSetAtom(isFinishRegistrationModalVisibleAtom);
  const isLoggedIn = useIsLoggedIn();
  const isRegisteringAfterDirectPurchase = useRegistrationStore(state => state.isRegisteringAfterDirectPurchase);
  const registeredUserId = useRegistrationStore(state => state.userId);
  const registeredUserSubscription = useRegistrationStore(state => state.package_subscription);
  const step = useRegistrationStore(state => state.step);
  const errors = useRegistrationStore(useShallow(state => Object.values(state.errors).filter(error => !!error)));
  const interests = useRegistrationStore(state => state.interests);
  const canFinishRegistration = useMemo(() => !!interests.length && !errors.length, [interests, errors]);
  const setError = useRegistrationStore(state => state.setError);
  const validateInterestSelection = useRegistrationStore(state => state.validateInterestSelection);
  const goToNextPage = useRegistrationStore(state => state.goToNextPage);
  const registerCompany = useRegistrationStore(state => state.register);

  const {mutate: register, isPending} = useMutation({
    mutationKey: USER_KEYS.mUserList,
    mutationFn: () => registerCompany(),
    onSuccess: async response => {
      if (isRegisteringAfterDirectPurchase) {
        dispatchGoogleAnalyticsEvent(
          "registration_events",
          "registration_finished",
          `Successfully registered user #${registeredUserId}`,
          {
            registered_user_id: registeredUserId,
            package_name: registeredUserSubscription?.name,
            package_type: registeredUserSubscription?.package_type,
            package_category: registeredUserSubscription?.package_category,
            registered_via_payment_link: true,
          },
        );
      } else {
        const registeredUser = response?.data?.user;

        if (registeredUser)
          dispatchGoogleAnalyticsEvent(
            "registration_events",
            "registration_finished",
            `Successfully registered user #${registeredUser.id}`,
            {
              registered_user_id: registeredUser.id,
              hubspot_id: registeredUser.hubspot_id,
              registered_via_payment_link: false,
            },
          );
      }

      if (isRegisteringAfterDirectPurchase) {
        await refetchUser();
        setIsFinishRegistrationModalVisible(false);
      }

      navigate(`/${isRegisteringAfterDirectPurchase ? "welcome" : "image-upload"}/`, {replace: true});

      if (!isLoggedIn)
        login({
          email: useRegistrationStore.getState().email,
          password: useRegistrationStore.getState().password,
        });
    },
    onError: (error: AxiosError<Partial<Record<keyof TUserState, string[]>>>) => {
      if (error.response?.data?.email?.[0] === "Benutzer mit diesem email existiert bereits.")
        setError("email", "emailAlreadyExists");
    },
  });

  const proceedWithRegistrationSteps = useCallback(() => {
    if (step === "interest_selection") {
      validateInterestSelection() && register();
    } else {
      goToNextPage();
    }
  }, [step, validateInterestSelection, register, goToNextPage]);

  return (
    <div className={"flex flex-col items-center gap-5 " + (step !== "credentials" ? "pb-10" : "")}>
      {step === "interest_selection" ? (
        <>
          <Button
            onClick={proceedWithRegistrationSteps}
            isDisabled={!canFinishRegistration || isPending}
            className={`h-14 w-48 ${isInsideModal ? "sm:min-w-[360px]" : ""}`}
            buttonStyle="Gradient">
            {isPending ? <Loader /> : t("createAccount")}
          </Button>
          {!!errors.length && (
            <div className="bg-pretty-pink-200 my-2.5 w-full rounded-md p-2.5">
              <ul className="mb-0">
                {errors.map((error, idx) => (
                  <li key={idx}>{t(error)}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <Button
          className={`h-14 w-48 ${isInsideModal ? "sm:min-w-[360px]" : ""}`}
          buttonStyle="Blue"
          onClick={proceedWithRegistrationSteps}>
          {t("Next")}
        </Button>
      )}
      {step === "interest_selection" && (
        <span className="text-neutral-grey-dark text-center text-sm font-medium">
          {t("createAccountDisclaimer")}
          <TextButton
            onClick={() => {
              window.open("/terms-conditions/", "_blank");
            }}
            text={"Refluenced " + t("ToS")}></TextButton>{" "}
          {t("and")}{" "}
          <TextButton
            className="text-primary-dark-solid hover:text-primary-medium-solid"
            onClick={() => {
              window.open("/privacy-policy/", "_blank");
            }}
            text={t("privacy_policy")}
          />
          {t("allowContact")}
        </span>
      )}
    </div>
  );
};

export default RegistrationBottomView;
